import { useState, useEffect, useRef, useCallback } from "react";
import { useViewAmbientContext } from "../Ambients/AmbientsContext";
import { LoadingCard } from "../../../../../../../_metronic/elements/Loading";
import { useViewLandContext } from "../ViewLandContext";
import { openAlert } from "../../../../../../../_metronic/elements/NotificationAlert";
import { useErrorHandler } from "../../../../../../../_metronic/helpers/ErrorHandler";
import * as GisDataAdapter from "../../../services/GisDataAdapter";
import { useTranslation } from "react-i18next";
import { useLandOperationSummary } from "../useLandOperationSummary";
import { useViewImageContext } from "../Images/ViewImageContext";
import {
  buildLayerDownloadBaseName,
  downloadBlob,
  downloadPossiblyZippedSingleFile,
  renameZipEntries,
} from "./downloadArchive";
import {
  buildYieldMapDownloadCanvas,
  buildVisualLayerCanvas,
  buildVisualLayerPdfCanvas,
  canvasToPdfBlob,
  canvasToPngBlob,
} from "./visualLayerExport";
import {
  getDefaultDownloadFormat,
  getDefaultDownloadLayer,
} from "./layerDownloadSelection";
import {withExplicitRemoteSensingSource} from "../Images/remoteSensingConsumerPayload";

const VECTOR_FORMAT_OPTIONS = [
  { value: "GeoJSON", label: "GeoJSON" },
  { value: "KML", label: "KML" },
  { value: "ESRI Shapefile", label: "Shape" },
];

const VISUAL_FORMAT_OPTIONS = [
  { value: "PNG", label: "PNG" },
  { value: "PDF", label: "PDF" },
];

const AMBIENT_FORMAT_OPTIONS = [...VECTOR_FORMAT_OPTIONS, ...VISUAL_FORMAT_OPTIONS];
const YIELD_MAP_FORMAT_OPTIONS = [...VECTOR_FORMAT_OPTIONS, ...VISUAL_FORMAT_OPTIONS];
const SOIL_MAP_FORMAT_OPTIONS = [...VECTOR_FORMAT_OPTIONS, ...VISUAL_FORMAT_OPTIONS];

const getYieldMapMoistureTarget = (yieldMap) => {
  const target = Number(
    yieldMap?.moistureTreatment?.outputCommercialTargetPercent
      ?? yieldMap?.moisture_treatment?.output_commercial_target_percent
  );

  return Number.isFinite(target) && target >= 0 && target < 100 ? target : null;
};

const getYieldMapLayerName = (yieldMap, t) => {
  const moistureTarget = getYieldMapMoistureTarget(yieldMap);

  return moistureTarget === null
    ? t("layers:yield_map.legacy")
    : t("layers:yield_map.normalized", { target: moistureTarget });
};

const getFormatOptionsForLayer = (layer) => {
  if (!layer) {
    return [];
  }

  if (layer === "altimetry") {
    return VISUAL_FORMAT_OPTIONS;
  }

  if (["ambient", "ambient_hd", "image_ambient", "image_ambient_hd"].includes(layer)) {
    return AMBIENT_FORMAT_OPTIONS;
  }

  if (layer === "yield_map") {
    return YIELD_MAP_FORMAT_OPTIONS;
  }

  if (layer === "soil_map") {
    return SOIL_MAP_FORMAT_OPTIONS;
  }

  return VECTOR_FORMAT_OPTIONS;
};

const isVisualFormat = (format) => ["PNG", "PDF"].includes(format);
const isSingleFileVectorFormat = (format) => format === "KML";
const isDirectBackendFormat = (format) => ["KML", "PNG", "PDF"].includes(format);
const getDirectFileMimeType = (format) => ({
  KML: "application/vnd.google-earth.kml+xml",
  PDF: "application/pdf",
  PNG: "image/png",
})[format] || "application/octet-stream";

const MobileWrapSelect = ({
  label,
  placeholder,
  value,
  options,
  disabled = false,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(null);
  const [menuPlacement, setMenuPlacement] = useState("down");
  const containerRef = useRef(null);
  const selectedOption = options.find((option) => option.value === value);

  const updateMenuLayout = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    let scrollParent = container.parentElement;

    while (scrollParent && scrollParent !== document.body) {
      const overflowY = window.getComputedStyle(scrollParent).overflowY;

      if (overflowY === "auto" || overflowY === "scroll") {
        break;
      }

      scrollParent = scrollParent.parentElement;
    }

    const boundaryRect =
      scrollParent && scrollParent !== document.body
        ? scrollParent.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };
    const menuGap = 3;
    const maxMenuHeight = 238;
    const minUsefulMenuHeight = 96;
    const spaceBelow = Math.max(0, boundaryRect.bottom - containerRect.bottom - menuGap);
    const spaceAbove = Math.max(0, containerRect.top - boundaryRect.top - menuGap);
    const nextPlacement = spaceBelow < maxMenuHeight && spaceAbove > spaceBelow ? "up" : "down";
    const availableSpace = nextPlacement === "up" ? spaceAbove : spaceBelow;
    const nextMaxHeight = Math.max(
      minUsefulMenuHeight,
      Math.min(maxMenuHeight, Math.floor(availableSpace))
    );

    setMenuPlacement((current) => (current === nextPlacement ? current : nextPlacement));
    setMenuMaxHeight((current) => (current === nextMaxHeight ? current : nextMaxHeight));
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!(event.target instanceof Node) || !containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleLayoutChange = () => updateMenuLayout();
    const animationFrame = window.requestAnimationFrame(updateMenuLayout);

    window.addEventListener("resize", handleLayoutChange);
    window.addEventListener("scroll", handleLayoutChange, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", handleLayoutChange);
      window.removeEventListener("scroll", handleLayoutChange, true);
    };
  }, [open, updateMenuLayout]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  return (
    <div
      ref={containerRef}
      className={`view-land-mobile-select ${open ? "is-open" : ""} ${menuPlacement === "up" ? "menu-up" : ""}`.trim()}
    >
      <span className="form-label fs-7 fw-bold">{label}</span>
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          if (!open) {
            updateMenuLayout();
          }

          setOpen((current) => !current);
        }}
        className="btn btn-light view-land-mobile-select__trigger"
      >
        <span
          className={`view-land-mobile-select__trigger-copy ${selectedOption ? "" : "is-placeholder"}`.trim()}
        >
          {selectedOption?.label || placeholder}
        </span>
        <span
          className={`view-land-mobile-select__chevron ${open ? "is-open" : ""}`.trim()}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="view-land-mobile-select__menu"
          role="listbox"
          aria-label={label}
          style={menuMaxHeight ? { maxHeight: `${menuMaxHeight}px` } : undefined}
        >
          {options.map((option) => {
            const isSelected = value === option.value;
            const isDisabled = disabled || option.enabled === false;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={isDisabled}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`view-land-mobile-select__option ${isSelected ? "active" : ""} ${option.enabled === false ? "is-unavailable" : ""}`.trim()}
              >
                <span className="view-land-mobile-select__option-label">{option.label}</span>
                {option.enabled === false && (
                  <span className="view-land-mobile-select__option-hint">
                    {option.hint || option.disabledLabel}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LayersChild = () => {
  const [layer, setLayer] = useState(null);
  const [format, setFormat] = useState(null);
  const [yieldMaps, setYieldMaps] = useState([]);
  const [yieldMapId, setYieldMapId] = useState(null);
  const [yieldMapsLoading, setYieldMapsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { errorHandler } = useErrorHandler();
  const { t } = useTranslation();
  const viewAmbientContext = useViewAmbientContext();
  const viewLandContext = useViewLandContext();
  const { ensureLandSoilMap, getLandSoilMapState } = useViewImageContext();
  const landOperations = useLandOperationSummary();
  const landId = viewLandContext.land?.land_id;
  const soilMapState = getLandSoilMapState(landId);
  const soilMapSelectionLoading = layer === "soil_map" && soilMapState.status !== "available";
  const yieldMapOptions = yieldMaps.map((yieldMap) => ({
    value: yieldMap.id,
    label: `${yieldMap.campaign || "-"} - ${yieldMap.name || t("layers:yield_map.unnamed")}`,
    hint: getYieldMapLayerName(yieldMap, t),
  }));
  const layerOptions = [
    ...landOperations.layers.options,
    {
      value: "yield_map",
      label: t("layers:layer_type.yield_map"),
      enabled: yieldMaps.length > 0,
      hint: yieldMaps.length > 0
        ? t("layers:hints.yield_map_ready")
        : t("layers:hints.yield_map_missing"),
    },
  ].map((option) => ({
    ...option,
    disabledLabel:
      option.value === "yield_map"
        ? t("layers:layer_type.missing_yield_map")
        : option.value === "soil_map"
          ? t("layers:layer_type.missing_soil_map")
          : t("layers:layer_type.missing_ambient"),
  }));
  const formatOptions = getFormatOptionsForLayer(layer);
  const defaultLayer = getDefaultDownloadLayer({
    activeImageType: landOperations.image.imageType,
    options: layerOptions,
  });

  useEffect(() => {
    const selectedLayerIsAvailable = layerOptions.some(
      (option) => option.value === layer && option.enabled !== false
    );

    if (!selectedLayerIsAvailable) {
      setLayer(defaultLayer);
    }
  }, [defaultLayer, layer, layerOptions]);

  useEffect(() => {
    if (layer !== "soil_map" || !landId || soilMapState.status !== "idle") {
      return;
    }

    ensureLandSoilMap(landId).catch(() => {});
  }, [ensureLandSoilMap, landId, layer, soilMapState.status]);

  useEffect(() => {
    const selectedFormatIsAvailable = formatOptions.some(
      (option) => option.value === format
    );

    if (!selectedFormatIsAvailable) {
      setFormat(
        getDefaultDownloadFormat({
          layer,
          options: formatOptions,
        })
      );
    }
  }, [format, formatOptions, layer]);

  useEffect(() => {
    let ignore = false;

    const loadYieldMaps = async () => {
      const landId = viewLandContext.land?.land_id;
      if (!landId) {
        setYieldMaps([]);
        setYieldMapId(null);
        return;
      }

      setYieldMapsLoading(true);
      try {
        const response = await GisDataAdapter.listLandYieldMaps(landId);
        if (ignore) {
          return;
        }
        const nextYieldMaps = response?.data?.yieldMaps || [];
        setYieldMaps(nextYieldMaps);
        setYieldMapId((current) => (
          nextYieldMaps.some((yieldMap) => yieldMap.id === current)
            ? current
            : nextYieldMaps[0]?.id || null
        ));
      } catch (error) {
        if (!ignore) {
          if (error?.response?.status !== 404) {
            console.error(error);
          }
          setYieldMaps([]);
          setYieldMapId(null);
        }
      } finally {
        if (!ignore) {
          setYieldMapsLoading(false);
        }
      }
    };

    loadYieldMaps();

    return () => {
      ignore = true;
    };
  }, [viewLandContext.land?.land_id]);

  useEffect(() => {
    if (layer !== "yield_map") {
      return;
    }

    if (!yieldMapId && yieldMaps[0]?.id) {
      setYieldMapId(yieldMaps[0].id);
    }
  }, [layer, yieldMapId, yieldMaps]);

  const download = async () => {
    const effectiveFormat = format;
    if (layer === "ambient_hd" && !viewAmbientContext.canAccessHistoricalHd) return;

    if (layer == null) {
      openAlert("danger", t("layers:errors.title"), t("layers:errors.missing_layer"));
      return;
    }

    if (effectiveFormat == null) {
      openAlert("danger", t("layers:errors.title"), t("layers:errors.missing_format"));
      return;
    }

    setLoading(true);
    let response;

    try {
      const selectedLayerOption = layerOptions.find((option) => option.value === layer);
      const downloadBaseName = buildLayerDownloadBaseName({
        layerName: selectedLayerOption?.label || t("layers:layer_type.placeholder"),
        fieldName: viewLandContext.land.field.name,
        landName: viewLandContext.land.name,
      });

      if (layer === "yield_map") {
        if (!yieldMapId) {
          openAlert("danger", t("layers:errors.title"), t("layers:errors.missing_yield_map"));
          setLoading(false);
          return;
        }

        const selectedYieldMap = yieldMaps.find((item) => item.id === yieldMapId);
        const yieldMapLayerName = getYieldMapLayerName(selectedYieldMap, t);
        const yieldMapBaseName = buildLayerDownloadBaseName({
          layerName: selectedYieldMap?.campaign
            ? `${yieldMapLayerName} ${selectedYieldMap.campaign}`
            : yieldMapLayerName,
          fieldName: viewLandContext.land.field.name,
          landName: viewLandContext.land.name,
        });

        if (isVisualFormat(effectiveFormat)) {
          const [pngResponse, gridResponse] = await Promise.all([
            GisDataAdapter.downloadLandYieldMap(
              viewLandContext.land.land_id,
              yieldMapId,
              { driver: "PNG" }
            ),
            GisDataAdapter.downloadLandYieldMap(
              viewLandContext.land.land_id,
              yieldMapId,
              { driver: "GRID" }
            ),
          ]);

          if (!pngResponse?.ok || !gridResponse?.ok) {
            throw new Error("yield-map-visual-export-unavailable");
          }

          const grid = JSON.parse(await gridResponse.data.text());
          const downloadCanvas = await buildYieldMapDownloadCanvas({
            campaign: selectedYieldMap?.campaign,
            grid,
            imageBlob: pngResponse.data,
            labels: {
              average: t("images:legend.average"),
              highest: t("images:legend.highest_yield"),
              layer: yieldMapLayerName,
              lowest: t("images:legend.lowest_yield"),
              title: t("images:legend.title"),
            },
            land: viewLandContext.land,
            layerName: yieldMapLayerName,
          });
          const fileBlob = effectiveFormat === "PDF"
            ? canvasToPdfBlob(
                await buildVisualLayerPdfCanvas({
                  canvas: downloadCanvas,
                  fieldName: viewLandContext.land.field.name,
                  landName: viewLandContext.land.name,
                  title: `${yieldMapLayerName} ${selectedYieldMap?.campaign || ""}`.trim(),
                }),
                yieldMapBaseName
              )
            : await canvasToPngBlob(downloadCanvas);

          downloadBlob({
            blob: fileBlob,
            fileName: `${yieldMapBaseName}.${effectiveFormat.toLowerCase()}`,
          });
          openAlert("success", t("layers:success.title"), t("layers:success.message"));
          setLoading(false);
          return;
        }

        response = await errorHandler(GisDataAdapter.downloadLandYieldMap(
          viewLandContext.land.land_id,
          yieldMapId,
          { driver: effectiveFormat }
        ));

        if (response?.ok) {
          const responseBlob = new Blob([response.data], {
            type: isDirectBackendFormat(effectiveFormat)
              ? getDirectFileMimeType(effectiveFormat)
              : "application/zip",
          });

          if (effectiveFormat === "KML") {
            await downloadPossiblyZippedSingleFile({
              blob: responseBlob,
              baseName: yieldMapBaseName,
              extension: "kml",
              mimeType: getDirectFileMimeType(effectiveFormat),
            });
          } else if (effectiveFormat === "ESRI Shapefile") {
            const renamedZip = await renameZipEntries({
              blob: responseBlob,
              baseName: yieldMapBaseName,
            });
            downloadBlob({
              blob: renamedZip,
              fileName: `${yieldMapBaseName}.zip`,
            });
          } else {
            downloadBlob({
              blob: responseBlob,
              fileName: `${yieldMapBaseName}.${effectiveFormat.toLowerCase()}`,
            });
          }

          openAlert("success", t("layers:success.title"), t("layers:success.message"));
          setLoading(false);
          return;
        }
      }

      if (isVisualFormat(effectiveFormat)) {
        const downloadCanvas = await buildVisualLayerCanvas({
          canAccessHistoricalHd: viewAmbientContext.canAccessHistoricalHd,
          land: viewLandContext.land,
          labels: {
            coverage: t("images:soil_map.coverage"),
            dataset: t("images:soil_map.dataset"),
            memory: t("images:soil_map.memory"),
            scale: t("images:soil_map.scale"),
            source: t("images:soil_map.source"),
            title: t("images:legend.title"),
            unknownUnit: t("images:soil_map.unknown_unit"),
          },
          layer,
          layerName: selectedLayerOption?.label || t("layers:layer_type.placeholder"),
        });
        const fileBlob = effectiveFormat === "PDF"
          ? canvasToPdfBlob(
              await buildVisualLayerPdfCanvas({
                canvas: downloadCanvas,
                fieldName: viewLandContext.land.field.name,
                landName: viewLandContext.land.name,
                title: selectedLayerOption?.label || t("layers:layer_type.placeholder"),
              }),
              downloadBaseName
            )
          : await canvasToPngBlob(downloadCanvas);
        const extension = effectiveFormat.toLowerCase();

        downloadBlob({
          blob: fileBlob,
          fileName: `${downloadBaseName}.${extension}`,
        });
        openAlert("success", t("layers:success.title"), t("layers:success.message"));
        setLoading(false);
        return;
      }

      if (layer === 'layer') {
        response = await errorHandler((GisDataAdapter.downloadLandLayer(viewLandContext.land.land_id, {
          driver: effectiveFormat
        })));
      } else {
        if (layer === "ambient"){
          response = await errorHandler(GisDataAdapter.downloadLandAmbient(
            viewLandContext.land.land_id,
            withExplicitRemoteSensingSource(
              {driver: effectiveFormat},
              viewAmbientContext.ambientData,
              {ambientType: "ambient"}
            )
          ));
        } else if (layer === "image_ambient"){
          response = await errorHandler(GisDataAdapter.downloadLandImageAmbient(
            viewLandContext.land.land_id,
            withExplicitRemoteSensingSource(
              {driver: effectiveFormat},
              viewAmbientContext.imageAmbientData,
              {ambientType: "imageAmbient"}
            )
          ));
        } else if (layer === "ambient_hd") {
          response = await errorHandler(GisDataAdapter.downloadLandAmbientHd(
            viewLandContext.land.land_id,
            withExplicitRemoteSensingSource(
              {driver: effectiveFormat},
              viewAmbientContext.ambientHdData,
              {ambientType: "ambientHd"}
            )
          ));
        } else if (layer === "image_ambient_hd") {
          response = await errorHandler(GisDataAdapter.downloadLandImageAmbientHd(
            viewLandContext.land.land_id,
            withExplicitRemoteSensingSource(
              {driver: effectiveFormat},
              viewAmbientContext.imageAmbientHdData,
              {ambientType: "imageAmbientHd"}
            )
          ));
        } else if (layer === "soil_map") {
          response = await errorHandler(GisDataAdapter.downloadLandSoilMap(
            viewLandContext.land.land_id,
            {driver: effectiveFormat}
          ));
        }
      }

      if (response?.ok) {
        const responseBlob = new Blob([response.data], {
          type: isSingleFileVectorFormat(effectiveFormat)
            ? getDirectFileMimeType(effectiveFormat)
            : "application/zip",
        });

        if (isSingleFileVectorFormat(effectiveFormat)) {
          await downloadPossiblyZippedSingleFile({
            blob: responseBlob,
            baseName: downloadBaseName,
            extension: effectiveFormat.toLowerCase(),
            mimeType: getDirectFileMimeType(effectiveFormat),
          });

          openAlert('success', t("layers:success.title"), t("layers:success.message"));
          setLoading(false);
          return;
        }

        const renamedZip = await renameZipEntries({
          blob: responseBlob,
          baseName: downloadBaseName,
        });

        downloadBlob({
          blob: renamedZip,
          fileName: `${downloadBaseName}.zip`,
        });

        openAlert('success', t("layers:success.title"), t("layers:success.message"));
      }
    } catch (error) {
      console.error(error);
      openAlert("danger", t("layers:errors.title"), t("layers:errors.download"));
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  useEffect(() => {
    setLayer(null);
    setFormat(null);
    setYieldMapId(null);
  }, [viewLandContext.land?.land_id]);

  if (viewAmbientContext.ambientSectionState === "loading") {
    return <LoadingCard />
  }

  return (
    <div className="view-land-mobile-state-stack view-land-layers-panel">
      <div className="view-land-mobile-state-card">
        <MobileWrapSelect
          key={viewLandContext.land?.land_id}
          label={t("layers:layer_type.label")}
          placeholder={t("layers:layer_type.placeholder")}
          value={layer}
          options={layerOptions}
          disabled={loading || yieldMapsLoading}
          onChange={setLayer}
        />

        {layer === "yield_map" && (
          <MobileWrapSelect
            key={`${viewLandContext.land?.land_id}-yield-map`}
            label={t("layers:yield_map.label")}
            placeholder={t("layers:yield_map.placeholder")}
            value={yieldMapId}
            options={yieldMapOptions}
            disabled={loading || yieldMapsLoading || yieldMaps.length === 0}
            onChange={setYieldMapId}
          />
        )}

        <MobileWrapSelect
          key={`${viewLandContext.land?.land_id}-format`}
          label={t("layers:format.label")}
          placeholder={t("layers:format.placeholder")}
          value={format}
          options={formatOptions}
          disabled={loading || !layer || soilMapSelectionLoading}
          onChange={setFormat}
        />

        <button
          onClick={download}
          type="button"
          disabled={
            loading ||
            !layer ||
            !format ||
            soilMapSelectionLoading ||
            (layer === "yield_map" && !yieldMapId)
          }
          className="btn btn-primary"
          data-kt-indicator={loading ? 'on' : 'off'}
        >
          <span className="indicator-label">
            {t("layers:buttons.download")}
          </span>
          <span className="indicator-progress">
            {t("layers:buttons.downloading")} <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
          </span>
        </button>
      </div>
    </div>
  );
};

const Layers = () => {
  return <LayersChild />
}

export default Layers;
